import { Store } from "vuex";
import { store } from "@/shared-store";
import { IVuexState } from "@/apps/merchant3/shared-models/IVuex";
import { ProductVersion } from "@/apps/merchant3/types/ProductVersion";
import { CartActiveFeatures } from "@/apps/merchant3/types/CartActiveFeatures";
import { SessionStorageEnum } from "@/apps/merchant3/enums/sessionStorageEnum";
import { OriginationChannelEnum } from "@/apps/merchant3/enums/originationChannelEnum";

import { getCountryCode } from "@/apps/merchant3/helpers/country";
export { getCountryCode, isValidCountryCode } from "@/apps/merchant3/helpers/country";

export enum FlowType {
  D2C = "d2c",
  ECOMMERCE = "ecommerce",
  KIOSK = "kiosk",
  STANDARD = "store",
}

const VALID_FLOW_TYPES = Object.values(FlowType);

/**
 * Detects current flow type from URL path
 * Supports both new (/:country/:flow/*) and legacy (/merchant3/*, /d2c/*) URL structures
 */
export const getFlowTypeFromUrl = (pathname?: string): FlowType | null => {
  const path = pathname ?? window.location.pathname;

  // New URL structure: /:country_code/:flow/*
  const newPatternMatch = path.match(/^\/[a-z]{2}\/([a-z]+)/i);
  if (newPatternMatch) {
    const flow = newPatternMatch[1].toLowerCase();
    if (VALID_FLOW_TYPES.includes(flow as FlowType)) {
      return flow as FlowType;
    }
  }

  // Legacy URL structure detection (for backwards compatibility)
  if (path.includes("/d2c")) {
    return FlowType.D2C;
  }

  if (path.includes("/ecommerce")) {
    return FlowType.ECOMMERCE;
  }

  if (path.includes("/kiosk") || path.includes("/self-onboarding")) {
    return FlowType.KIOSK;
  }

  if (path.includes("/store") || path.includes("/merchant3")) {
    return FlowType.STANDARD;
  }

  return null;
};

/**
 * Detects flow type from session storage (origination channel)
 */
export const getFlowTypeFromSession = (): FlowType | null => {
  const originationChannel = sessionStorage.getItem(
    SessionStorageEnum.OriginationChannel
  );

  if (originationChannel === OriginationChannelEnum.DirectToCustomer) {
    return FlowType.D2C;
  }

  // Check for kiosk
  if (sessionStorage.getItem("isKiosk") === "true") {
    return FlowType.KIOSK;
  }

  // Check for ecommerce via product version in session
  const productVersion = sessionStorage.getItem(
    SessionStorageEnum.CartProductVersion
  );
  if (productVersion === ProductVersion.M3_ECOMMERCE) {
    return FlowType.ECOMMERCE;
  }

  if (originationChannel === OriginationChannelEnum.MerchantConsole) {
    return FlowType.STANDARD;
  }

  return null;
};

/**
 * Infers flow type from cart data (used for cart recovery / Finish Sale)
 * This handles pre-deployment carts that may not have explicit flow type
 */
export const inferFlowTypeFromCart = (cart: {
  activeFeatures?: string[] | null;
  productVersion?: string | null;
}): FlowType => {
  // 1. Check cart active features for D2C
  if (
    cart.activeFeatures?.includes(
      CartActiveFeatures.CHECKOUT_ON_DIRECT_TO_CUSTOMER
    )
  ) {
    return FlowType.D2C;
  }

  // 2. Check productVersion for ecommerce
  if (cart.productVersion === ProductVersion.M3_ECOMMERCE) {
    return FlowType.ECOMMERCE;
  }

  // 3. Check productVersion for kiosk/self-onboarding
  if (cart.productVersion === ProductVersion.M3_SELF_ONBOARDING) {
    return FlowType.KIOSK;
  }

  // 4. Default to standard (handles pre-deployment carts)
  return FlowType.STANDARD;
};

/**
 * Gets current flow type from multiple sources
 * Priority: URL > session > cart state > default (standard)
 */
export const getCurrentFlowType = (): FlowType => {
  // 1. Try URL first
  const urlFlow = getFlowTypeFromUrl();
  if (urlFlow) {
    return urlFlow;
  }

  // 2. Try session
  const sessionFlow = getFlowTypeFromSession();
  if (sessionFlow) {
    return sessionFlow;
  }

  // 3. Try cart state from store
  const vuexStore = store as unknown as Store<IVuexState>;
  if (vuexStore?.state?.carts) {
    return inferFlowTypeFromCart({
      activeFeatures: vuexStore.state.carts.activeFeatures,
      productVersion: vuexStore.state.carts.productVersion,
    });
  }

  // 4. Default to standard
  return FlowType.STANDARD;
};

/**
 * Builds a URL path with the new structure: /:country/:flow/:step
 */
export const buildFlowPath = (
  flow: FlowType,
  step: string,
  countryCode?: string
): string => {
  const country = countryCode ?? getCountryCode();
  const normalizedStep = step.startsWith("/") ? step.slice(1) : step;
  return `/${country}/${flow}/${normalizedStep}`;
};

/**
 * Gets the base path for a flow type with country
 */
export const getFlowBasePath = (
  flow: FlowType,
  countryCode?: string
): string => {
  const country = countryCode ?? getCountryCode();
  return `/${country}/${flow}`;
};

/**
 * Checks if a path matches the new URL structure: /:country/:flow/*
 */
export const isNewUrlStructure = (pathname?: string): boolean => {
  const path = pathname ?? window.location.pathname;
  return /^\/[a-z]{2}\/(store|d2c|ecommerce|kiosk)/i.test(path);
};

/**
 * Checks if a path matches the legacy URL structure
 */
export const isLegacyUrlStructure = (pathname?: string): boolean => {
  const path = pathname ?? window.location.pathname;
  return path.includes("/merchant3") || path.startsWith("/d2c/");
};
